import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { getMyBookings, cancelBooking } from "../../api/booking.api";
import { Skeleton, SkeletonText } from "../../components/ui/Skeleton";
import { EmptyState } from "../../components/ui/EmptyState";
import type { Booking } from "@abejon/shared";

type UserBooking = Booking & {
  serviceName: string;
  businessName: string;
  businessSlug: string;
};

function formatTime(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${m.toString().padStart(2, "0")} ${period}`;
}

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}min` : `${hrs}h`;
}

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  confirmed: { bg: "bg-blue-50", text: "text-blue-700", label: "Confirmed" },
  completed: { bg: "bg-green-50", text: "text-green-700", label: "Completed" },
  cancelled_by_business: { bg: "bg-red-50", text: "text-red-700", label: "Cancelled by business" },
  cancelled_by_customer: { bg: "bg-orange-50", text: "text-orange-700", label: "Cancelled" },
  no_show: { bg: "bg-gray-100", text: "text-gray-600", label: "No-show" },
};

export function UserBookingsPage() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [bookingsList, setBookingsList] = useState<UserBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [loadKey, setLoadKey] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setFetchError(false);

    async function load() {
      try {
        const result = await getMyBookings();
        if (!cancelled) setBookingsList(result.bookings);
      } catch {
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user, loadKey]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-lg py-8 px-4">
        <SkeletonText className="mb-6 h-7 w-36" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  async function handleCancel(booking: UserBooking) {
    if (!confirm(`Cancel your booking at ${booking.businessName}?`)) return;

    setActionLoading(booking.id);
    setMessage("");
    try {
      const { booking: updated } = await cancelBooking(booking.id);
      setBookingsList((prev) =>
        prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b))
      );
      setMessage("Booking cancelled");
    } catch {
      setMessage("Failed to cancel booking");
    } finally {
      setActionLoading(null);
    }
  }

  // Split into upcoming and past
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const upcoming = bookingsList.filter(
    (b) => b.date >= todayStr && b.status === "confirmed"
  );
  const past = bookingsList.filter(
    (b) => b.date < todayStr || b.status !== "confirmed"
  );

  return (
    <div className="mx-auto max-w-lg py-8 px-4">
      <h1 className="mb-6 text-2xl font-bold">My Bookings</h1>

      {message && (
        <p className={`mb-4 text-sm ${message.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
          {message}
        </p>
      )}

      {fetchError ? (
        <div className="py-12 text-center">
          <p className="mb-3 text-sm text-gray-500">Failed to load bookings.</p>
          <button
            onClick={() => setLoadKey((k) => k + 1)}
            className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
          >
            Retry
          </button>
        </div>
      ) : loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-2">
                  <SkeletonText className="h-4 w-36" />
                  <SkeletonText className="h-4 w-28" />
                  <SkeletonText className="h-3 w-44" />
                  <SkeletonText className="h-3 w-24" />
                </div>
                <Skeleton className="h-7 w-16 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      ) : bookingsList.length === 0 ? (
        <EmptyState
          title="No bookings yet"
          description="Book an appointment to get started."
          action={{ label: "Explore businesses", href: "/explore" }}
        />
      ) : (
        <>
          {/* Upcoming */}
          {upcoming.length > 0 && (
            <section className="mb-8">
              <h2 className="mb-3 text-lg font-semibold">Upcoming</h2>
              <div className="space-y-3">
                {upcoming.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    onCancel={handleCancel}
                    isActioning={actionLoading === booking.id}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Past / Other */}
          {past.length > 0 && (
            <section>
              <h2 className="mb-3 text-lg font-semibold">Past</h2>
              <div className="space-y-3">
                {past.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    onCancel={handleCancel}
                    isActioning={actionLoading === booking.id}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function BookingCard({
  booking,
  onCancel,
  isActioning,
}: {
  booking: UserBooking;
  onCancel: (b: UserBooking) => void;
  isActioning: boolean;
}) {
  const style = STATUS_STYLES[booking.status] ?? STATUS_STYLES.confirmed;
  const canCancel = booking.status === "confirmed";

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {/* Business name + status */}
          <div className="flex items-center gap-2">
            <Link
              to={`/b/${booking.businessSlug}`}
              className="text-sm font-semibold text-gray-900 hover:underline"
            >
              {booking.businessName}
            </Link>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}>
              {style.label}
            </span>
          </div>

          {/* Service */}
          <p className="mt-1 text-sm text-gray-700">{booking.serviceName}</p>

          {/* Date + time */}
          <p className="mt-0.5 text-sm text-gray-500">
            {formatDate(booking.date)} · {formatTime(booking.startTime)} – {formatTime(booking.endTime)}
          </p>

          {/* Duration + Price */}
          <p className="mt-1 text-xs text-gray-400">
            {formatDuration(booking.durationMinutes)} · {formatPrice(booking.priceInCents)}
          </p>
        </div>

        {canCancel && (
          <button
            onClick={() => onCancel(booking)}
            disabled={isActioning}
            className="rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
